import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { Button, Table } from '@flos/react-ui';
import type { RowInformation } from '../../../../config/flavourConfig';
import TableRow from '../../../../shared_components/TableRow/TableRow';
import { getBBRtableConfig } from './PrhBBRTableUtil';
import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import CallMeModal from '../../../../shared_components/CallMeModal/CallMeModal';
import { useState } from 'react';
import { shouldShowRemoveAndContinue, shouldShowCallMe } from './HouseBBRUtil/BBRValidation';
import RemoveModal from '../../../../shared_components/RemoveModal/RemoveModal';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableFromAllOnsaCoverables } from '../../../../utils/coverablesUtil';

const HouseBBRCorrectInformation = ({
    bbrData,
    goNext,
    goPrevious,
    setIsEditingBBR
}: {
    bbrData: PrhHouseOnsaDTO | PrhHolidayHomeOnsaDTO;
    goNext: () => void;
    goPrevious: () => void;
    setIsEditingBBR: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { updateCoverableInformation } = useUpdateCoverableInformation();

    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const currentCoverable = getCoverableFromAllOnsaCoverables(
        coverables,
        currentProduct.fixedId,
        currentProduct.lobCode
    );

    const coverableProperties = getBBRtableConfig(bbrData, getHippoText);
    const tableRows = coverableProperties.filter((tableInfo) => !tableInfo.hidden);
    const [showCallMeModal, setShowCallMeModal] = useState<boolean>(false);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);

    const onNext = (): void => {
        updateCoverableInformation({
            ...bbrData
        });
        if (shouldShowRemoveAndContinue(bbrData)) {
            setShowRemoveModal(true);
            return;
        }
        if (shouldShowCallMe(bbrData)) {
            setShowCallMeModal(true);
            return;
        }
        goNext();
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            continueButtonText={getHippoText(
                'DSS-COVERABLE-INFORMATION-STEP-BBR-CONFIRM-INFORMATION'
            )}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-HEADER')}
                subheader={getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-SUBHEADER')}
            >
                <div className="u-display-flex u-flex-justify-center">
                    <Button
                        iconShape="edit"
                        theme="ghost"
                        onClick={() => {
                            setIsEditingBBR(true);
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-CORRECT-INFORMATION')}
                    </Button>
                </div>
                <div className="u-spacing-inset-m--stretch-max">
                    <Table mobileDirection="horizontal" className="u-spacing-stack-sm">
                        <Table.Body>
                            {tableRows.map((rowInfo: RowInformation) => {
                                return <TableRow key={rowInfo.name} rowInfo={rowInfo} />;
                            })}
                        </Table.Body>
                    </Table>
                </div>
                <div className="u-display-flex u-flex-justify-center">
                    <Button
                        iconShape="edit"
                        theme="ghost"
                        onClick={() => {
                            setIsEditingBBR(true);
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-INFORMATION-STEP-BBR-CORRECT-INFORMATION')}
                    </Button>
                </div>
            </CoverableDetailsTemplate>
            {showRemoveModal && currentCoverable && (
                <RemoveModal
                    coverable={currentCoverable}
                    title={getHippoText('DSS-COVERABLE-INFORMATION-REMOVEBBR-TITLE')}
                    description={getHippoText('DSS-COVERABLE-INFORMATION-REMOVEBBR-BODY')}
                    isRemoveAndContinueModal
                    showModal={showRemoveModal}
                    showCloseButton
                    closeModal={() => {
                        setShowRemoveModal(false);
                    }}
                />
            )}
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_call_request"
                    showModal={showCallMeModal}
                    closeModal={() => {
                        setShowCallMeModal(false);
                    }}
                    title={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-HEADER')}
                    description={
                        getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-SUBHEADER') +
                        ' ' +
                        getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-BOOK-CALL-SUBHEADER')
                    }
                />
            )}
        </WizardPagetemplate>
    );
};

export default HouseBBRCorrectInformation;
